// ─────────────────────────────────────────────────────────────────────────────
// TourCoachmark — the instruction UI for one tour step.
//
// Two states:
//   • EXPANDED  — full card (title, text, Skip + Next, Step X of N), placed in
//                 free space near the target (never overlapping the spotlight).
//                 Next is ALWAYS available so the user can advance manually.
//   • COLLAPSED — a small pulsing icon the user can DRAG anywhere. Tapping it
//                 (without dragging) re-expands the card. The spotlight ring
//                 (drawn by TourRunner) stays on the target, so the user always
//                 sees WHAT to do without text blocking content.
//
// Starts expanded; auto-collapses after AUTO_COLLAPSE_MS or on tap-outside.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { X, ArrowRight, HelpCircle } from 'lucide-react';

const AUTO_COLLAPSE_MS = 5000;
const CARD_W = 340;
const GAP = 18;            // space between target and card
const MARGIN = 10;         // min distance from viewport edge
const SPOT_PAD = 16;       // keep clear of the spotlight ring/padding
const CLEARANCE = GAP + SPOT_PAD;  // total min gap between card and target
const BOTTOM_NAV_H = 64;   // app's fixed bottom navigation height
const CHIP = 48;           // chip diameter
const DRAG_THRESHOLD = 6;  // px of movement before a press counts as a drag

// Minimum height a card must have to be readable. If neither the band above nor
// below the target can hold this much, the target is basically full-screen and
// avoiding it is pointless — we center the card instead (overlapping is fine).
const MIN_READABLE_H = 210;

// Place the card. Preferred: in the free band above/below the target (no
// overlap). Fallback: if no band is tall enough to read, center it on screen.
function placeBox(rect, w, requestedH) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  // Free vertical space below / above the target, after reserving CLEARANCE.
  const bandBelow = vh - (rect.bottom + CLEARANCE) - MARGIN;
  const bandAbove = (rect.top - CLEARANCE) - MARGIN;
  const biggestBand = Math.max(bandBelow, bandAbove);

  // Centered fallback for full-screen / very tall targets (no room either side).
  if (biggestBand < MIN_READABLE_H) {
    const h = Math.min(Math.max(requestedH, MIN_READABLE_H), vh - 2 * MARGIN);
    return {
      top: Math.max(MARGIN, (vh - h) / 2),
      left: Math.max(MARGIN, (vw - w) / 2),
      maxHeight: h,
      centered: true,
    };
  }

  const useBelow = bandBelow >= bandAbove;
  const band = useBelow ? bandBelow : bandAbove;
  const h = Math.min(requestedH, band);

  let top;
  if (useBelow) {
    top = rect.bottom + CLEARANCE;
  } else {
    // Anchor the card's BOTTOM edge CLEARANCE above the target, so it can never
    // overlap the target (this was the bug: the card covered the top half of the
    // Trade nav button, eating taps).
    top = rect.top - CLEARANCE - h;
  }
  let left = rect.left + rect.width / 2 - w / 2;
  left = Math.max(MARGIN, Math.min(left, vw - w - MARGIN));
  return { top: Math.max(MARGIN, top), left, maxHeight: h, centered: false };
}

// Default resting spot for the collapsed chip: bottom-right, above the nav.
function defaultChipPos() {
  return {
    left: window.innerWidth - CHIP - MARGIN,
    top: window.innerHeight - CHIP - BOTTOM_NAV_H - MARGIN,
  };
}

export default function TourCoachmark({
  rect, title, text, stepNumber, totalSteps, showNext, isLast, onNext, onSkip,
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [cardH, setCardH] = useState(200);
  const [chipPos, setChipPos] = useState(defaultChipPos);
  const [dragging, setDragging] = useState(false);   // chip is being dragged
  const [overDrop, setOverDrop] = useState(false);    // chip is over the X drop-zone
  const cardRef = useRef(null);
  const timerRef = useRef(null);

  // Drag bookkeeping for the chip.
  const dragRef = useRef({ dragging: false, moved: false, dx: 0, dy: 0 });

  // Auto-collapse timer (resets whenever we re-expand).
  useEffect(() => {
    if (collapsed) return;
    timerRef.current = setTimeout(() => setCollapsed(true), AUTO_COLLAPSE_MS);
    return () => clearTimeout(timerRef.current);
  }, [collapsed]);

  // Measure card height for placement.
  useLayoutEffect(() => {
    if (!collapsed && cardRef.current) setCardH(cardRef.current.offsetHeight);
  }, [collapsed, title, text]);

  // Tap-outside collapses the expanded card.
  useEffect(() => {
    if (collapsed) return;
    const onDown = (e) => {
      if (cardRef.current && !cardRef.current.contains(e.target)) setCollapsed(true);
    };
    document.addEventListener('pointerdown', onDown, { capture: true });
    return () => document.removeEventListener('pointerdown', onDown, { capture: true });
  }, [collapsed]);

  // Height of the bottom strip that acts as the "drag here to close" drop-zone.
  const DROP_ZONE_H = 120;
  const isInDropZone = (clientY) => clientY >= window.innerHeight - DROP_ZONE_H;

  // ── Chip drag handlers ──────────────────────────────────────────────────────
  const onChipPointerDown = useCallback((e) => {
    e.stopPropagation();
    const d = dragRef.current;
    d.dragging = true;
    d.moved = false;
    d.startX = e.clientX;     // press origin — used for drag-vs-tap threshold
    d.startY = e.clientY;
    d.dx = e.clientX - chipPos.left;   // pointer offset within the chip
    d.dy = e.clientY - chipPos.top;
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }, [chipPos]);

  const onChipPointerMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d.dragging) return;
    // Promote to a real drag once movement passes the threshold from the origin.
    if (!d.moved &&
        (Math.abs(e.clientX - d.startX) > DRAG_THRESHOLD ||
         Math.abs(e.clientY - d.startY) > DRAG_THRESHOLD)) {
      d.moved = true;
      setDragging(true);   // reveal the drop-zone once a real drag begins
    }
    let left = e.clientX - d.dx;
    let top = e.clientY - d.dy;
    left = Math.max(MARGIN, Math.min(left, window.innerWidth - CHIP - MARGIN));
    top = Math.max(MARGIN, Math.min(top, window.innerHeight - CHIP - MARGIN));
    setChipPos({ left, top });
    if (d.moved) setOverDrop(isInDropZone(e.clientY));
  }, []);

  const onChipPointerUp = useCallback((e) => {
    const d = dragRef.current;
    const wasDrag = d.moved;
    const droppedToClose = wasDrag && isInDropZone(e.clientY);
    d.dragging = false;
    d.moved = false;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    setDragging(false);
    setOverDrop(false);

    if (droppedToClose) {
      onSkip();                          // drag-to-bottom dismissed the tour
    } else if (!wasDrag) {
      setCollapsed(false);               // a tap re-opens the details
    }
  }, [onSkip]);

  if (!rect) return null;

  // ── Collapsed: draggable pulsing chip (+ drag-to-bottom-to-close drop-zone) ──
  if (collapsed) {
    return (
      <>
        {/* Drop-zone: appears at the bottom only while dragging. Release the chip
            over it to close the tour. Highlights when the chip is over it. */}
        {dragging && (
          <div
            aria-hidden
            style={{
              position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 99999,
              height: DROP_ZONE_H, pointerEvents: 'none',
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: 6,
              background: overDrop
                ? 'linear-gradient(to top, rgba(239,68,68,0.38), rgba(239,68,68,0))'
                : 'linear-gradient(to top, rgba(0,0,0,0.45), rgba(0,0,0,0))',
              transition: 'background 0.15s ease',
            }}
          >
            <div style={{
              width: overDrop ? 56 : 48, height: overDrop ? 56 : 48, borderRadius: '50%',
              background: overDrop ? '#ef4444' : 'rgba(255,255,255,0.14)',
              border: '2px solid rgba(255,255,255,0.85)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.15s ease',
            }}>
              <X size={overDrop ? 28 : 22} color="#fff" />
            </div>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', opacity: 0.9 }}>
              {overDrop ? 'Release to close' : 'Drag here to close'}
            </span>
          </div>
        )}

        <div
          className="tour-coachmark"
          onPointerDown={onChipPointerDown}
          onPointerMove={onChipPointerMove}
          onPointerUp={onChipPointerUp}
          role="button"
          aria-label={`Tour step ${stepNumber} of ${totalSteps} — tap to read, drag to move, drag to bottom to close`}
          style={{
            position: 'fixed', zIndex: 100000,
            top: chipPos.top, left: chipPos.left,
            width: CHIP, height: CHIP, borderRadius: '50%',
            background: overDrop
              ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
              : 'linear-gradient(135deg, #6366F1 0%, #4f46e5 100%)',
            border: '2px solid rgba(255,255,255,0.85)',
            color: '#fff',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 16px rgba(99,102,241,0.55)',
            cursor: dragging ? 'grabbing' : 'grab', touchAction: 'none', userSelect: 'none',
            animation: dragging ? 'none' : 'tourChipPulse 1.8s ease-in-out infinite',
            transition: 'background 0.15s ease',
          }}
        >
          {overDrop ? (
            <X size={20} color="#fff" />
          ) : (
            <>
              <HelpCircle size={16} color="#fff" />
              <span style={{ fontSize: 9, fontWeight: 800, marginTop: 1 }}>{stepNumber}/{totalSteps}</span>
            </>
          )}
        </div>
      </>
    );
  }

  // ── Expanded: full instruction card (Next ALWAYS available) ─────────────────
  const pos = placeBox(rect, CARD_W, cardH);
  return (
    <div
      ref={cardRef}
      className="tour-coachmark"
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'fixed', zIndex: 100000,
        top: pos.top, left: pos.left, width: CARD_W,
        maxHeight: pos.maxHeight, overflowY: 'auto',
        background: '#13141a',
        border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: 16,
        boxShadow: '0 16px 48px rgba(0,0,0,0.6)',
        padding: '18px 20px',
        animation: 'tourCardIn 0.22s cubic-bezier(0.32,0.72,0,1)',
      }}
    >
      <button
        onClick={onSkip}
        aria-label="Skip tour"
        style={{ position: 'absolute', top: 10, right: 10, padding: 4, color: 'rgba(255,255,255,0.4)' }}
      >
        <X size={16} />
      </button>

      <p style={{
        fontSize: 10, fontWeight: 800, textTransform: 'uppercase',
        letterSpacing: '0.08em', color: '#6366F1', marginBottom: 7,
      }}>
        Step {stepNumber} of {totalSteps}
      </p>

      <h3 style={{
        fontSize: 17, fontWeight: 800, color: '#eceef5',
        letterSpacing: '-0.01em', marginBottom: 8, paddingRight: 20,
      }}>
        {title}
      </h3>

      <p style={{ fontSize: 13.5, lineHeight: 1.65, color: 'rgba(255,255,255,0.62)' }}>
        {text}
      </p>

      {/* Hint for click/route steps — the action also lives on the highlight. */}
      {!showNext && (
        <p style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.4)', fontStyle: 'italic', marginTop: 8 }}>
          Tap the highlighted area, or use Next below.
        </p>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 14 }}>
        <button
          onClick={onSkip}
          style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.45)', padding: '6px 4px' }}
        >
          Skip tour
        </button>

        {/* Next is ALWAYS shown so the user can always advance manually. */}
        <button
          onClick={onNext}
          style={{
            display: 'flex', alignItems: 'center', gap: 5,
            fontSize: 12.5, fontWeight: 800, color: '#6366F1',
            background: 'linear-gradient(135deg, #6366F122 0%, #6366F10e 100%)',
            border: '1px solid #6366F145',
            borderRadius: 10, padding: '8px 18px', cursor: 'pointer',
          }}
        >
          {isLast ? 'Finish' : 'Next'} <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
}
